// @tennis-matches/services/platform/active-tenant
// Kanonischer aktiver Tenant-Scope für die gesamte Plattform.
// SoT: localStorage `ttb_active_tenant_id` (+ Event für Same-Tab-Sync).

export const ACTIVE_TENANT_STORAGE_KEY = "ttb_active_tenant_id";
export const ACTIVE_TENANT_CHANGED_EVENT = "tm:active-tenant-changed";

/** Legacy App-Keys — werden beim Schreiben mitgespiegelt, beim Lesen als Fallback genutzt. */
export const LEGACY_ACTIVE_TENANT_STORAGE_KEYS = Object.freeze([
  "tm:belegung:tenantId",
  "tm:kalender:tenantId",
  "tm:winterdoppel:tenantId",
]);

function __asStr(v) {
  return String(v ?? "").trim();
}

/**
 * Explizite Mandantenwahl: gespeicherte ID nur wenn Mitgliedschaft existiert;
 * bei genau einem Tenant dieser; sonst leer (fail closed).
 */
function resolveExplicitMembershipTenantId(input = {}) {
  const memberships = [
    ...new Set(
      (Array.isArray(input.membershipTenantIds)
        ? input.membershipTenantIds
        : []
      )
        .map(__asStr)
        .filter(Boolean),
    ),
  ];
  const stored = __asStr(input.storedTenantId);
  if (stored && memberships.includes(stored)) return stored;
  if (memberships.length === 1) return memberships[0];
  return "";
}

function __safeGet(key) {
  if (typeof localStorage === "undefined") return "";
  try {
    return __asStr(localStorage.getItem(key));
  } catch {
    return "";
  }
}

function __safeSet(key, value) {
  if (typeof localStorage === "undefined") return;
  try {
    const next = __asStr(value);
    if (next) localStorage.setItem(key, next);
    else localStorage.removeItem(key);
  } catch {
    /* private mode / quota */
  }
}

/**
 * Liest den gespeicherten aktiven Tenant (kanonisch, sonst Legacy-Fallback).
 * @returns {string}
 */
export function getActiveTenantId() {
  const canonical = __safeGet(ACTIVE_TENANT_STORAGE_KEY);
  if (canonical) return canonical;
  for (const key of LEGACY_ACTIVE_TENANT_STORAGE_KEYS) {
    const legacy = __safeGet(key);
    if (legacy) return legacy;
  }
  return "";
}

/**
 * Setzt den aktiven Tenant global und spiegelt Legacy-Keys.
 * @param {string} tenantId
 * @returns {string} normalisierte ID
 */
export function setActiveTenantId(tenantId) {
  const next = __asStr(tenantId);
  const prev = getActiveTenantId();
  __safeSet(ACTIVE_TENANT_STORAGE_KEY, next);
  for (const key of LEGACY_ACTIVE_TENANT_STORAGE_KEYS) {
    __safeSet(key, next);
  }
  if (next !== prev && typeof window !== "undefined") {
    try {
      window.dispatchEvent(
        new CustomEvent(ACTIVE_TENANT_CHANGED_EVENT, {
          detail: { tenantId: next, previousTenantId: prev },
        }),
      );
    } catch {
      /* noop */
    }
  }
  return next;
}

/**
 * UI-/App-Auflösung: gespeicherte ID wenn Mitglied, sonst Single-Tenant, sonst erster Club.
 * Persistiert den gewählten Default, damit alle Apps denselben Scope sehen.
 *
 * @param {{ membershipTenantIds?: string[]|null, persistDefault?: boolean }} [input]
 * @returns {string}
 */
export function resolveActiveTenantId(input = {}) {
  const memberships = [
    ...new Set(
      (Array.isArray(input.membershipTenantIds)
        ? input.membershipTenantIds
        : []
      )
        .map(__asStr)
        .filter(Boolean),
    ),
  ];
  const stored = getActiveTenantId();
  const explicit = resolveExplicitMembershipTenantId({
    membershipTenantIds: memberships,
    storedTenantId: stored,
  });
  if (explicit) {
    if (explicit !== stored && input.persistDefault !== false) {
      setActiveTenantId(explicit);
    }
    return explicit;
  }
  const fallback = memberships[0] || "";
  if (fallback && input.persistDefault !== false) {
    setActiveTenantId(fallback);
  }
  return fallback;
}

/**
 * Abonniert Änderungen am aktiven Tenant (CustomEvent + storage).
 * @param {(tenantId: string) => void} listener
 * @returns {() => void}
 */
export function subscribeActiveTenantId(listener) {
  if (typeof listener !== "function") return () => {};
  if (typeof window === "undefined") return () => {};

  const onCustom = (event) => {
    const fromDetail = __asStr(event?.detail?.tenantId);
    listener(fromDetail || getActiveTenantId());
  };
  const onStorage = (event) => {
    if (!event) return;
    const key = String(event.key || "");
    if (
      key !== ACTIVE_TENANT_STORAGE_KEY &&
      !LEGACY_ACTIVE_TENANT_STORAGE_KEYS.includes(key)
    ) {
      return;
    }
    listener(getActiveTenantId());
  };

  window.addEventListener(ACTIVE_TENANT_CHANGED_EVENT, onCustom);
  window.addEventListener("storage", onStorage);
  return () => {
    window.removeEventListener(ACTIVE_TENANT_CHANGED_EVENT, onCustom);
    window.removeEventListener("storage", onStorage);
  };
}
